import React from "react";
import { MapPin, User, FileStack, ChevronRight, MessageSquareReply } from "lucide-react";
import { TASK_TYPES, fmtDate } from "@/lib/tasks";
import { PriorityBadge, StatusBadge, DueLabel } from "@/components/tasks/TaskBadges";

function Row({ t, today, onOpen }) {
  const place = [t.project_name, t.building, t.floor, t.room].filter(Boolean).join(" · ");
  const accent = t.is_overdue ? "border-l-red-500" : t.priority === "kritisch" && !t.is_closed ? "border-l-orange-500" : "border-l-transparent";
  return (
    <button onClick={() => onOpen(t)} data-testid={`task-row-${t.key}`}
      className={`w-full text-left grid grid-cols-1 lg:grid-cols-[90px_minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1fr)_110px_120px_20px] gap-x-4 gap-y-1.5 items-center px-4 py-3 border-b border-slate-100 border-l-4 ${accent} hover:bg-slate-50 transition-colors`}>
      <div className="flex lg:block items-center gap-2"><PriorityBadge p={t.priority} testid={`task-prio-${t.key}`} />
        <span className="lg:hidden text-[11px] text-slate-500">{TASK_TYPES[t.type]}</span></div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          {t.number && <span className="font-mono-tech text-xs text-slate-500 shrink-0">{t.number}</span>}
          <span className="font-semibold text-slate-900 truncate" data-testid={`task-title-${t.key}`}>{t.title || "(ohne Titel)"}</span>
          {t.feedback_pending && <MessageSquareReply className="h-3.5 w-3.5 text-violet-600 shrink-0" title="Rückmeldung angefragt" />}
        </div>
        <div className="text-[11px] text-slate-400 mt-0.5 hidden lg:block">{TASK_TYPES[t.type]}{t.subtype && t.subtype !== t.type ? ` · ${t.subtype}` : ""} · erstellt {fmtDate(t.created_at)} · aktualisiert {fmtDate(t.updated_at)}</div>
      </div>
      <div className="min-w-0 text-xs text-slate-600">
        <div className="flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0 text-slate-400" /><span className="truncate">{place || "—"}</span></div>
        {t.plan_name && <div className="flex items-center gap-1 truncate text-slate-400 mt-0.5"><FileStack className="h-3 w-3 shrink-0" /><span className="truncate">{t.plan_name}{t.position_number ? ` · Marker ${t.position_number}` : ""}</span></div>}
      </div>
      <div className="text-xs text-slate-600 flex items-center gap-1 min-w-0" data-testid={`task-assignee-${t.key}`}>
        <User className="h-3 w-3 shrink-0 text-slate-400" /><span className="truncate">{t.assignee_name || t.legacy_responsible || <span className="text-slate-400 italic">nicht zugewiesen</span>}</span>
      </div>
      <div><DueLabel t={t} today={today} testid={`task-due-${t.key}`} /></div>
      <div><StatusBadge t={t} testid={`task-status-${t.key}`} /></div>
      <ChevronRight className="h-4 w-4 text-slate-300 hidden lg:block" />
    </button>
  );
}

export default function TaskList({ items, today, onOpen, loading, error, emptyText }) {
  if (error) return <div className="p-8 text-center text-sm text-red-600" data-testid="task-list-error">{error}</div>;
  if (loading) return <div className="p-4 space-y-2" data-testid="task-list-loading">{[0, 1, 2, 3].map((i) => <div key={i} className="h-14 rounded-lg bg-slate-100 animate-pulse" />)}</div>;
  if (!items.length) return <div className="p-10 text-center text-sm text-slate-400" data-testid="task-list-empty">{emptyText}</div>;
  return (
    <div data-testid="task-list">
      <div className="hidden lg:grid grid-cols-[90px_minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1fr)_110px_120px_20px] gap-x-4 px-4 py-2 text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100 border-l-4 border-l-transparent">
        <span>Priorität</span><span>Aufgabe</span><span>Objekt / Ort / Plan</span><span>Verantwortlich</span><span>Fällig</span><span>Status</span><span />
      </div>
      {items.map((t) => <Row key={t.key} t={t} today={today} onOpen={onOpen} />)}
    </div>
  );
}
